import { observer } from '@formily/react';
import { IAnyObject, isVacuous } from '@yimoka/shared';
import { BaseStore, getEntityStore } from '@yimoka/store';
import React, { useEffect } from 'react';

import { useDeepMemo } from '../../hooks/deep-memo';
import { useEntityConfig } from '../../hooks/entity-config';

import { IEntityAddProps } from './add';
import { Entity } from './base';

export const EntityBatchAdd = observer((props: IEntityAddProps) => {
  const { config, store, scope, defaultValues = { data: [] }, schema, entity, instanceKey, dispose, ...args } = props;
  const curConfig = useEntityConfig(config ?? entity?.config);

  const curStore = useDeepMemo(() => {
    if (store instanceof BaseStore) {
      if (!isVacuous(defaultValues)) {
        store.setDefaultValues(defaultValues);
      }
      return store;
    }
    if (entity) {
      const options: IAnyObject = { ...(store ?? {}), instanceKey };
      if (!isVacuous(defaultValues)) {
        options.defaultValues = { ...options.defaultValues, ...defaultValues };
      }
      return entity.getOrCreate('batchAdd', options);
    }
    if (!isVacuous(defaultValues)) {
      if (!store) {
        return getEntityStore({ defaultValues }, 'batchAdd', curConfig);
      }
      store.defaultValues = { ...store.defaultValues, ...defaultValues };
    }
    return getEntityStore(store, 'batchAdd', curConfig);
  }, [store, curConfig, defaultValues, entity, instanceKey]);

  useEffect(() => {
    if (!dispose || !entity || store instanceof BaseStore) return;
    return () => {
      entity.dispose('batchAdd', instanceKey);
    };
  }, [dispose, entity, store, instanceKey]);

  const curScope = useDeepMemo(() => ({ $entity: entity, $config: curConfig, ...scope }), [entity, curConfig, scope]);

  const curSchema = useDeepMemo(() => schema ?? {
    type: 'object',
    properties: {
      form: {
        type: 'void',
        'x-component': 'StoreForm',
        properties: {
          data: {
            type: 'array',
            'x-component': 'JSONEditor',
            'x-component-props': {
              valueType: 'object',
              height: 600,
            },
          },
        },
      },
    },
  }, [schema]);

  return (
    <Entity
      {...args}
      schema={curSchema}
      scope={curScope}
      store={curStore}
    />
  );
});
